import { useCallback, useEffect, useRef, useState } from "react";
import api from "../services/api";
import { RECON } from "../services/apiConstant";
import { isImageFile } from "./useAIPhotoDescription";
import { compressImagesForAI } from "../utils/imageCompression";
import { presignUpload } from "../services/attachmentService";
import { SSEUnavailableError, isAbortError, streamSSE } from "../services/sseClient";

export interface ReconStageSuggestion {
  phase: string;
  stage: string;
  description: string;
  estimated_days: number;
  image_indices: number[];
}

export interface ReconSuggestionsResponse {
  suggestions: ReconStageSuggestion[];
}

/** A compressed image and the R2 key it was uploaded to (once). */
export interface UploadedImage {
  file: File;
  objectKey: string;
}

export interface ReconSuggestionsResult {
  suggestions: ReconStageSuggestion[];
  /** Uploaded images in the same order as `image_indices` refer to. Reuse these
   *  keys when attaching on submit — no second upload. */
  uploads: UploadedImage[];
  /** Images whose upload to R2 failed — surfaced so the user can retry them.
   *  The AI analysis ran on the successfully-uploaded subset. */
  failedUploads: File[];
}

export type AIAutofillPhase =
  | "compressing"
  | "uploading"
  | "queued"
  | "downloading"
  | "analyzing"
  | "matching"
  | "done"
  | "error";

/** Phases that can be "in flight" — i.e. where a failure can happen. */
export type AIAutofillWorkPhase = Exclude<AIAutofillPhase, "done" | "error">;

export interface AIAutofillProgress {
  phase: AIAutofillPhase;
  /** Number of photos in this run. */
  total: number;
  /** Photos uploaded to R2 so far. */
  uploaded?: number;
  /** Photos the backend has fetched from R2 so far. */
  downloaded?: number;
  /** Photos the backend couldn't fetch (still analyzed without them). */
  skipped?: number;
  /** Server-reported seconds since the AI call started (heartbeat). */
  elapsedS?: number;
  /** The request had to wait for an AI slot — shows the "Waiting for AI" step. */
  wasQueued?: boolean;
  /** Set on `error`: the phase that was active when it failed. */
  failedPhase?: AIAutofillWorkPhase;
  errorMessage?: string;
}

/** Event payloads of POST /ai/recon-suggestions/stream. */
interface ReconSuggestionStreamEvents {
  received: { total: number };
  queued: Record<string, never>;
  downloading: { done: number; total: number; skipped?: number };
  analyzing: { elapsed_s: number; images?: number };
  result: ReconSuggestionsResponse;
  done: { elapsed_s?: number };
  error: { status?: number; message?: string };
}

class AIStreamError extends Error {
  readonly status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.name = "AIStreamError";
    this.status = status;
  }
}

const abortError = () => new DOMException("AI auto-fill was cancelled.", "AbortError");

export function useAIReconSuggestions() {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<AIAutofillProgress | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  // Abort an in-flight run if the component using the hook unmounts.
  useEffect(() => () => controllerRef.current?.abort(), []);

  const update = useCallback((patch: Partial<AIAutofillProgress>) => {
    setProgress((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  /** Stop the current run. `suggestReconRequests` rejects with an AbortError. */
  const cancel = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  /** Let callers drive the post-response phases (`matching` → `done`). */
  const setPhase = useCallback(
    (phase: AIAutofillPhase) => update({ phase }),
    [update]
  );

  const resetProgress = useCallback(() => setProgress(null), []);

  const suggestReconRequests = useCallback(
    async (files: File[]): Promise<ReconSuggestionsResult> => {
      const imageFiles = files.filter(isImageFile);
      if (imageFiles.length === 0) throw new Error("No image files provided.");

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const { signal } = controller;
      const throwIfAborted = () => {
        if (signal.aborted) throw abortError();
      };

      const total = imageFiles.length;
      let currentPhase: AIAutofillWorkPhase = "compressing";
      const enter = (phase: AIAutofillWorkPhase, patch: Partial<AIAutofillProgress> = {}) => {
        currentPhase = phase;
        update({ phase, ...patch });
      };

      setIsLoading(true);
      setProgress({ phase: "compressing", total });
      try {
        // Resize in the background (web worker), then upload each image to R2
        // exactly ONCE. The bytes never go through our backend; we send keys.
        const compressed = await compressImagesForAI(imageFiles);
        throwIfAborted();

        enter("uploading", { uploaded: 0 });
        let uploadedCount = 0;
        const results = await Promise.all(
          compressed.map(async (file) => {
            const res = await presignUpload(file);
            if (!signal.aborted && res.success) update({ uploaded: ++uploadedCount });
            return res;
          })
        );
        throwIfAborted();

        const uploads: UploadedImage[] = [];
        const failedUploads: File[] = [];
        results.forEach((res, i) => {
          if (res.success && res.object_key) {
            uploads.push({ file: compressed[i], objectKey: res.object_key });
          } else {
            // Don't abort the whole autofill for one bad upload — collect it so the
            // UI can prompt a retry, and analyze the images that did upload.
            failedUploads.push(compressed[i]);
          }
        });

        if (uploads.length === 0) {
          throw new Error("All image uploads failed. Please try again.");
        }

        // Keys line up with image_indices in the response.
        const body = { image_keys: uploads.map((u) => u.objectKey) };
        // Filled by the `result` event (or the fallback POST).
        const outcome: { suggestions?: ReconStageSuggestion[] } = {};

        try {
          await streamSSE<ReconSuggestionStreamEvents>({
            path: RECON.reconSuggestionsByKeysStream,
            body,
            signal,
            handlers: {
              received: () => enter("downloading", { downloaded: 0 }),
              queued: () => enter("queued", { wasQueued: true }),
              downloading: ({ done, skipped }) =>
                enter("downloading", { downloaded: done, skipped }),
              analyzing: ({ elapsed_s }) => enter("analyzing", { elapsedS: elapsed_s }),
              result: (data) => {
                outcome.suggestions = data.suggestions ?? [];
              },
              error: ({ status, message }) => {
                throw new AIStreamError(message || "AI analysis failed.", status);
              },
            },
          });
          if (!outcome.suggestions) {
            throw new Error("The AI stream ended without a result.");
          }
        } catch (error) {
          if (!(error instanceof SSEUnavailableError)) throw error;
          // Older backend (no stream endpoint) or the stream couldn't open:
          // fall back to the one-shot POST. The UI shows a local timer.
          throwIfAborted();
          enter("analyzing", { elapsedS: undefined });
          const response = await api.post<ReconSuggestionsResponse>(
            RECON.reconSuggestionsByKeys,
            body,
            { timeout: 120000, signal } // Gemini analysis can take a while
          );
          outcome.suggestions = response.data.suggestions;
        }

        // Next step is the caller's client-side stage matching.
        enter("matching");
        return { suggestions: outcome.suggestions ?? [], uploads, failedUploads };
      } catch (error) {
        if (isAbortError(error) || signal.aborted) {
          setProgress(null);
          throw abortError();
        }
        const message = error instanceof Error ? error.message : "AI auto-fill failed.";
        update({ phase: "error", failedPhase: currentPhase, errorMessage: message });
        throw error;
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
        setIsLoading(false);
      }
    },
    [update]
  );

  return { suggestReconRequests, isLoading, progress, cancel, setPhase, resetProgress };
}
